import React, { useState, useEffect } from "react";
import RemainingDaysBadge from "./common/RemainingDaysBadge";
import AdExtensionModal from "./AdExtensionModal";
import SimpleKeywordEditModal from "./KeywordEditModal";
import QuantityIncreaseModal from "./QuantityIncreaseModal";
import TabNavigation from "../../../../../components/common/tabNavigation";
import {
  rejectAdAPI,
  getMissionHistoryAPI,
  getKeywordNameAPI,
  getNaverPlayStoreAPI,
  patchAdExtensionAPI,
} from "../../../../../api/index";
import { convertUTCToKoreaTime } from "../../../../../utils/helpers";
import { useAuth } from "../../../../../hooks/useAuth";
import "../AdDetailModal.css";

const PlaceAdDetail = ({
  isOpen,
  onClose,
  adData,
  keywordData, // MyAdvertisement에서 전달받은 키워드 데이터
  priceGradeData, // MyAdvertisement에서 전달받은 가격 등급 데이터
  getUserDetailAPI, // MyAdvertisement에서 전달받은 사용자 상세 정보 API 함수
  onSave, // 키워드 수정 완료 시 호출될 콜백
}) => {
  const { user } = useAuth();
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const [isKeywordEditModalOpen, setIsKeywordEditModalOpen] = useState(false);
  const [isQuantityIncreaseModalOpen, setIsQuantityIncreaseModalOpen] =
    useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [activeTab, setActiveTab] = useState("details"); // 탭 상태
  const [historyData, setHistoryData] = useState([]); // 내역 데이터
  const [historyLoading, setHistoryLoading] = useState(false); // 내역 로딩
  const [expandedHistoryIds, setExpandedHistoryIds] = useState(new Set()); // 펼쳐진 내역 ID
  const [keywordMap, setKeywordMap] = useState({}); // 키워드 ID -> 이름 맵
  const [refreshLoading, setRefreshLoading] = useState(false); // 다시 받아오기 로딩

  // 1회 단가 계산 - 결제금액 ÷ 신청수량 (기존 광고의 실제 단가)
  const getUnitPrice = (adData) => {
    const totalPrice = adData.price || 0;
    const totalQuantity = adData.totalQuantity || 0;
    return totalQuantity > 0 ? Math.round(totalPrice / totalQuantity) : 0;
  };

  const unitPrices = {
    [adData.ad_id || adData.id]: getUnitPrice(adData),
  };

  // username은 adData에서 가져오기 (광고 소유자 정보)
  const username = adData.username || adData.member_id || "";

  // 가격 등급 데이터는 props로 받은 것 사용
  const userData = {
    price_grade_detail: {
      data: priceGradeData || [],
    },
  };

  // 숫자 포맷팅 함수
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return num.toLocaleString();
  };

  // 날짜 포맷팅 함수
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return dateString.split("T")[0].replace(/-/g, ".");
  };

  // 상태 클래스 반환 함수
  const getStatusClass = (status) => {
    switch (status) {
      case "APPROVED":
        return "status-progress";
      case "PENDING":
        return "status-pending";
      case "PAUSED":
        return "status-paused";
      case "REJECTED":
        return "status-cancelled";
      case "REFUND":
        return "status-refund";
      case "DONE":
        return "status-ended";
      default:
        return "";
    }
  };

  // 상태 텍스트 반환 함수
  const getStatusText = (status) => {
    switch (status) {
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "PENDING":
        return "승인대기중";
      case "REJECTED":
        return "거절";
      case "REFUND":
        return "환불";
      case "DONE":
        return "종료";
      default:
        return status;
    }
  };

  // 서브키워드 가져오기 함수 (6개로 통일)
  const getSubKeywords = () => {
    // keywordData가 배열인 경우 SUB 타입 키워드들을 추출
    const subKeywords = keywordData?.filter((k) => k.type === "SUB") || [];
    const result = [];

    for (let i = 0; i < 6; i++) {
      result.push(subKeywords[i]?.name || "-");
    }

    return result;
  };

  // 총 결제 금액 계산 함수
  const calculateTotalPrice = () => {
    const price = adData.price || 0;
    const quantity = adData.totalQuantity || 0;
    return formatNumber(price * quantity);
  };

  // 광고 종료일(endDate)까지 남은 일수 (날짜만 비교)
  const getRemainingDaysUntilEnd = () => {
    if (!adData?.endDate) return 0;
    const end = new Date(adData.endDate);
    const endDateOnly = new Date(
      end.getFullYear(),
      end.getMonth(),
      end.getDate()
    );
    const now = new Date();
    const todayOnly = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );
    return Math.floor(
      (endDateOnly - todayOnly) / (24 * 60 * 60 * 1000)
    );
  };

  // 광고 취소 핸들러
  const handleCancelAd = async () => {
    if (!adData?.ad_id) {
      alert("광고 정보를 찾을 수 없습니다.");
      return;
    }

    const confirmCancel = window.confirm("정말로 이 광고를 취소하시겠습니까?");
    if (!confirmCancel) return;

    try {
      setIsCancelling(true);

      // rejectAdAPI 호출 (필요한 파라미터들 전달)
      const currentDeposit = user?.deposit || 0; // 로그인한 사용자의 현재 예치금
      const amount = adData.price || 0;
      const newDeposit = currentDeposit + amount;

      await rejectAdAPI(
        adData.ad_id,
        adData.webMemberId || adData.member_id,
        adData.missionName || adData.adName || "광고",
        newDeposit,
        amount,
        "REJECTED",
        adData.startDate,
        adData.endDate
      );

      alert("광고가 성공적으로 취소되었습니다.");
      onClose(); // 모달 닫기
      // 부모 컴포넌트에서 데이터 새로고침을 위해 onSave 호출
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("광고 취소 실패:", error);
      alert("광고 취소에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsCancelling(false);
    }
  };

  // 내역 데이터 로드 (내역 탭이 활성화될 때)
  useEffect(() => {
    if (activeTab === "history" && isOpen && adData?.ad_id) {
      loadHistoryData();
    }
  }, [activeTab, isOpen, adData?.ad_id]);

  // 내역 데이터 로드 함수
  const loadHistoryData = async () => {
    if (!adData?.ad_id) return;

    try {
      setHistoryLoading(true);
      const response = await getMissionHistoryAPI(adData.ad_id);
      const historyList = response.data?.data || [];
      setHistoryData(historyList);

      // 모든 키워드 ID 수집
      const keywordIds = new Set();
      historyList.forEach((history) => {
        if (history.beforeSnapshot) {
          if (history.beforeSnapshot.mainKeywordId) {
            keywordIds.add(history.beforeSnapshot.mainKeywordId);
          }
          if (history.beforeSnapshot.subKeywordIds) {
            history.beforeSnapshot.subKeywordIds.forEach((id) =>
              keywordIds.add(id)
            );
          }
        }
        if (history.afterSnapshot) {
          if (history.afterSnapshot.mainKeywordId) {
            keywordIds.add(history.afterSnapshot.mainKeywordId);
          }
          if (history.afterSnapshot.subKeywordIds) {
            history.afterSnapshot.subKeywordIds.forEach((id) =>
              keywordIds.add(id)
            );
          }
        }
      });

      // 키워드 이름 가져오기
      const map = {};
      if (keywordData && keywordData.length > 0) {
        keywordData.forEach((keyword) => {
          if (keyword.keyword_id) {
            map[keyword.keyword_id] = keyword.name;
          }
        });
      }

      const missingKeywordIds = Array.from(keywordIds).filter((id) => !map[id]);
      if (missingKeywordIds.length > 0) {
        try {
          const keywordResponse = await getKeywordNameAPI(
            null,
            null,
            missingKeywordIds,
            1,
            2000
          );
          const keywords = keywordResponse.data?.data || [];
          keywords.forEach((keyword) => {
            if (keyword.keyword_id && !map[keyword.keyword_id]) {
              map[keyword.keyword_id] = keyword.name;
            }
          });
        } catch (error) {
          console.error("키워드 이름 로드 실패:", error);
        }
      }

      setKeywordMap(map);
    } catch (error) {
      console.error("내역 데이터 로드 실패:", error);
      setHistoryData([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  // 내역 펼치기/접기 토글
  const toggleHistoryExpanded = (historyId) => {
    setExpandedHistoryIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(historyId)) {
        newSet.delete(historyId);
      } else {
        newSet.add(historyId);
      }
      return newSet;
    });
  };

  // 액션 타입 한글 변환
  const getActionLabel = (action) => {
    switch (action) {
      case "STATUS_CHANGED":
        return "상태 변경";
      case "UPDATED":
        return "업데이트";
      case "CREATED":
        return "생성";
      default:
        return action;
    }
  };

  // 상태 한글 변환
  const getStatusLabel = (status) => {
    switch (status) {
      case "PENDING":
        return "승인대기중";
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "REJECTED":
        return "거절";
      case "DONE":
        return "종료";
      case "REFUND":
        return "환불";
      default:
        return status;
    }
  };

  // 광고 데이터 새로고침 처리
  const handleRefreshAdData = async () => {
    if (!adData) {
      alert("광고 데이터를 찾을 수 없습니다.");
      return;
    }

    try {
      setRefreshLoading(true);

      if (adData.productUrl) {
        const response = await getNaverPlayStoreAPI(adData.productUrl);
        if (response.data.error) {
          console.error("크롤링 에러:", response.data.error);
          alert(`크롤링 실패: ${response.data.error}`);
          setRefreshLoading(false);
          return;
        }

        if (response.data.success && response.data.data) {
          const hasImageUrlChanged =
            adData.imageUrl !== response.data.data.imageUrl;
          const hasMissionNameChanged =
            adData.missionName !== response.data.data.placeName;

          if (hasImageUrlChanged || hasMissionNameChanged) {
            // memo 파싱
            let memoData = {};
            try {
              if (adData.memo) {
                memoData = JSON.parse(adData.memo);
              }
            } catch (error) {
              console.error("memo 파싱 실패:", error);
            }

            const extensionDataFix = {
              ad_id: adData.ad_id || adData.id,
            };

            if (hasImageUrlChanged) {
              extensionDataFix.imageUrl = response.data.data.imageUrl;
            }
            if (hasMissionNameChanged) {
              extensionDataFix.missionName = response.data.data.placeName;
            }

            // PAUSED 상태일 때 처리
            if (adData.status === "PAUSED") {
              extensionDataFix.status = "APPROVED";

              // pausedDate 배열 확인
              let pausedDateArray = memoData.pausedDate;
              if (!Array.isArray(pausedDateArray)) {
                // 배열이 아니면 빈 배열로 초기화
                pausedDateArray = [];
              }

              // pausedDate 배열의 마지막 항목 확인
              const lastPausedItem =
                pausedDateArray.length > 0
                  ? pausedDateArray[pausedDateArray.length - 1]
                  : null;

              // 마지막 항목이 있고, pausedDate가 있으며, changeToApproved가 아직 없는 경우에만 처리
              if (
                lastPausedItem &&
                lastPausedItem.pausedDate &&
                !lastPausedItem.changeToApproved
              ) {
                // changeToApproved: UTC 현재 시간
                const changeToApproved = new Date().toISOString();

                // pausedDate와 changeToApproved 날짜 추출 (당일 제외 계산을 위해)
                const pausedDateStr = lastPausedItem.pausedDate.includes("T")
                  ? lastPausedItem.pausedDate.split("T")[0]
                  : lastPausedItem.pausedDate.split(" ")[0];
                const changeToApprovedStr = changeToApproved.includes("T")
                  ? changeToApproved.split("T")[0]
                  : changeToApproved.split(" ")[0];

                // pausedDate와 changeToApproved 날짜 객체 생성
                const pausedDate = new Date(pausedDateStr + "T00:00:00");
                const changeToApprovedDate = new Date(
                  changeToApprovedStr + "T00:00:00"
                );

                // 당일 제외하고 날짜 차이 계산
                // 예: pausedDate가 12/03, changeToApproved가 12/05면
                // 12/04만 계산 (12/03과 12/05는 제외)
                const daysDiff = Math.floor(
                  (changeToApprovedDate - pausedDate) / (1000 * 60 * 60 * 24)
                );

                // 당일 제외: daysDiff가 1보다 크면 1을 빼고, 1 이하면 0
                const actualDays = daysDiff > 1 ? daysDiff - 1 : 0;

                // dailyQuantity와 cost 가져오기
                const dailyQuantity =
                  lastPausedItem.dailyQuantity || adData.dailyQuantity || 0;
                const cost =
                  lastPausedItem.cost ||
                  (adData.price && adData.totalQuantity
                    ? Math.round(adData.price / adData.totalQuantity)
                    : 0);

                // amount 계산: actualDays * dailyQuantity * cost
                const amount = actualDays * dailyQuantity * cost;

                // 마지막 항목에 changeToApproved와 amount 추가
                pausedDateArray[pausedDateArray.length - 1] = {
                  ...lastPausedItem,
                  changeToApproved: changeToApproved,
                  amount: amount,
                };

                // memoData에 업데이트된 pausedDate 배열 저장
                memoData.pausedDate = pausedDateArray;
                extensionDataFix.memo = JSON.stringify(memoData);
              } else {
                // pausedDate 배열이 없거나 마지막 항목이 없으면 새로 생성
                const dailyQuantity = adData.dailyQuantity || 0;
                const cost =
                  adData.price && adData.totalQuantity
                    ? Math.round(adData.price / adData.totalQuantity)
                    : 0;
                const changeToApproved = new Date().toISOString();
                const pausedDateValue = adData.updatedAt || changeToApproved;

                pausedDateArray.push({
                  pausedDate: pausedDateValue,
                  dailyQuantity: dailyQuantity,
                  cost: cost,
                  changeToApproved: changeToApproved,
                  amount: 0, // 당일이므로 0
                });

                memoData.pausedDate = pausedDateArray;
                extensionDataFix.memo = JSON.stringify(memoData);
              }
            }

            try {
              const updateResponse = await patchAdExtensionAPI(
                extensionDataFix
              );
              if (response.data.success && response.data.data) {
                console.log("updateResponse", updateResponse);
              }
              alert("데이터가 성공적으로 업데이트되었습니다.");
              if (onSave) {
                await onSave();
              }
            } catch (updateError) {
              console.error("업데이트 실패:", updateError);
              alert("업데이트 중 오류가 발생했습니다.");
            }
          } else {
            alert("변경할 데이터가 없습니다.");
            setRefreshLoading(false);
            return;
          }
        }
      }
    } catch (error) {
      console.error("변경사항 발견 광고 데이터 새로고침 실패:", error);
      alert("데이터를 새로고침하는데 실패했습니다. 다시 시도해주세요.");
    } finally {
      setRefreshLoading(false);
    }
  };

  if (!isOpen || !adData) return null;

  return (
    <>
      <div className="ad-detail-modal-overlay" onClick={onClose}>
        <div className="ad-detail-modal" onClick={(e) => e.stopPropagation()}>
          {/* 모달 헤더 */}
          <div className="modal-header">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "15px",
                width: "100%",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "15px",
                  flex: 1,
                }}
              >
                {/* 1-1-1. Image */}
                <div className="thumbnail-image">
                  <img
                    src={adData.imageUrl || "/logo192.png"}
                    alt={adData.missionName || "플레이스 이미지"}
                    className="product-thumbnail"
                    onError={(e) => {
                      e.target.src = "/logo192.png";
                    }}
                  />
                </div>

                {/* 1-1-2. Column */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "5px",
                    flex: 1,
                  }}
                >
                  {/* 1-1-2-1. Mission Name (플레이스명만) */}
                  <div className="product-name">
                    <a
                      href={adData.productUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="product-link"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "block",
                      }}
                      title={adData.missionName || "-"}
                    >
                      {adData.missionName || "-"}
                    </a>
                  </div>
                </div>

                {/* 1-1-3. Remaining Days */}
                <RemainingDaysBadge
                  startDate={adData.startDate}
                  endDate={adData.endDate}
                  status={adData.status}
                />

                {/* 1-1-4. Status */}
                <span
                  className={`status-badge ${getStatusClass(adData.status)}`}
                >
                  {getStatusText(adData.status)}
                </span>
              </div>

              {/* 1-1-5. 다시 받아오기 버튼 */}
              {user?.role !== "CHECKER" &&
                (adData.status === "PENDING" ||
                  adData.status === "PAUSED" ||
                  adData.status === "APPROVED") && (
                  <button
                    className="ad-refresh-btn"
                    onClick={handleRefreshAdData}
                    disabled={refreshLoading}
                    title="다시 받아오기"
                  >
                    {refreshLoading ? "받아오는 중..." : "다시 받아오기"}
                  </button>
                )}

              {/* 1-1-6. 광고연장버튼 (가장 우측에 고정) */}
              {user?.role !== "CHECKER" && (
                <button
                  className="extension-btn"
                  disabled={adData.status !== "APPROVED"}
                  onClick={() => setIsExtensionModalOpen(true)}
                >
                  광고연장
                </button>
              )}
            </div>
          </div>

          {/* 탭 네비게이션 */}
          <TabNavigation
            tabs={[
              { id: "details", label: "광고 상세 정보" },
              ...(user?.role !== "CHECKER"
                ? [
                    { id: "keywords", label: "키워드" },
                    { id: "history", label: "내역" },
                  ]
                : []),
            ]}
            activeTab={activeTab}
            onTabClick={setActiveTab}
          />

          {/* 상세 내용 */}
          <div className="modal-content">
            {activeTab === "details" && (
              <div className="form-grid">
                <div className="form-row">
                  <label>플레이스 URL</label>
                  <div className="form-value">
                    {adData.productUrl ? (
                      <a
                        href={adData.productUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "block",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                        }}
                        title={adData.productUrl}
                      >
                        {adData.productUrl}
                      </a>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <label>플레이스번호</label>
                  <div
                    className="form-value"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={adData.productId || "-"}
                  >
                    {adData.productId || "-"}
                  </div>
                </div>

                {user?.role !== "CHECKER" && (
                  <div className="form-row">
                    <label>미션 방법</label>
                    <div className="form-value">{adData.productName || "-"}</div>
                  </div>
                )}

                <div className="form-row">
                  <label>광고 시작일</label>
                  <div
                    className="form-value"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={formatDate(adData.startDate)}
                  >
                    {formatDate(adData.startDate)}
                  </div>
                </div>

                {user?.role !== "CHECKER" && (
                  <div className="form-row">
                    <label>1회 단가</label>
                    <div
                      className="form-value"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={`${formatNumber(
                        Math.round(adData.price / adData.totalQuantity)
                      )}원`}
                    >
                      {formatNumber(
                        Math.round(adData.price / adData.totalQuantity)
                      )}
                      원
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <label>총 수량</label>
                  <div
                    className="form-value"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={formatNumber(adData.totalQuantity)}
                  >
                    {formatNumber(adData.totalQuantity)}
                  </div>
                </div>

                <div className="form-row">
                  <label>일일수량</label>
                  <div
                    className="form-value"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={formatNumber(adData.dailyQuantity)}
                  >
                    {formatNumber(adData.dailyQuantity)}
                  </div>
                </div>

                {user?.role !== "CHECKER" && (
                  <div className="form-row">
                    <label>결제 금액</label>
                    <div
                      className="form-value"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={`${formatNumber(adData.price)}원`}
                    >
                      {formatNumber(adData.price)}원
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <label>마감 예정일</label>
                  <div
                    className="form-value"
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    title={formatDate(adData.endDate)}
                  >
                    {formatDate(adData.endDate)}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "keywords" && (
              <>
                {/* 키워드 정보 섹션 */}
                <div className="keyword-summary-section">
                  <div className="keyword-section-header">
                    <h4>키워드 정보</h4>
                    {(adData.status === "APPROVED" ||
                      adData.status === "PENDING") && (
                      <button
                        className="ad-detail-edit-keyword-btn"
                        onClick={() => {
                          setIsKeywordEditModalOpen(true);
                        }}
                      >
                        키워드 수정
                      </button>
                    )}
                  </div>
                  <div className="keyword-summary">
                    <div className="keyword-summary-item">
                      <span className="summary-label">메인 키워드:</span>
                      <span className="summary-value main-keyword">
                        {(() => {
                          const mainKeyword = keywordData?.find(
                            (k) => k.type === "MAIN"
                          );
                          return (
                            mainKeyword?.name ||
                            adData.mainKeywordName ||
                            "없음"
                          );
                        })()}
                      </span>
                    </div>
                    <div className="keyword-summary-item">
                      <span className="summary-label">서브 키워드:</span>
                      <div className="sub-keywords-list">
                        {getSubKeywords().filter((k) => k !== "-").length >
                        0 ? (
                          getSubKeywords()
                            .filter((k) => k !== "-")
                            .map((keyword, index) => (
                              <span key={index} className="sub-keyword-tag">
                                {keyword}
                              </span>
                            ))
                        ) : (
                          <span className="no-keywords">없음</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {activeTab === "history" && (
              <div className="ad-history-section">
                <h4>변경 내역</h4>
                <div className="ad-history-content">
                  {historyLoading ? (
                    <div style={{ textAlign: "center", padding: "40px" }}>
                      <p>내역을 불러오는 중...</p>
                    </div>
                  ) : historyData.length === 0 ? (
                    <p
                      style={{
                        color: "#6b7280",
                        textAlign: "center",
                        padding: "40px",
                      }}
                    >
                      변경 내역 데이터가 없습니다.
                    </p>
                  ) : (
                    <div className="ad-history-timeline">
                      {historyData.map((history) => {
                        const isExpanded = expandedHistoryIds.has(history._id);
                        const changedAt = history.changedAt
                          ? convertUTCToKoreaTime(history.changedAt)
                          : null;
                        const changedAtStr = changedAt
                          ? changedAt.toLocaleString("ko-KR", {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "";

                        return (
                          <div key={history._id} className="ad-history-entry">
                            <div
                              className="ad-history-entry-header"
                              onClick={() => toggleHistoryExpanded(history._id)}
                            >
                              <div className="ad-history-entry-header-left">
                                <span className="ad-history-entry-action">
                                  {getActionLabel(history.action)}
                                </span>
                                <span className="ad-history-entry-reason">
                                  {history.reason || "-"}
                                </span>
                                {history.admin_memo && (
                                  <span className="ad-history-entry-memo">
                                    ({history.admin_memo})
                                  </span>
                                )}
                              </div>
                              <div className="ad-history-entry-header-right">
                                <span className="ad-history-entry-date">
                                  {changedAtStr}
                                </span>
                                <span className="ad-history-entry-toggle">
                                  {isExpanded ? "▼" : "▶"}
                                </span>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="ad-history-entry-details">
                                <div className="ad-history-entry-changed-fields">
                                  <strong>변경된 필드:</strong>{" "}
                                  {history.changedFields?.join(", ") || "-"}
                                </div>
                                <div className="ad-history-entry-snapshots">
                                  <div className="snapshot-section">
                                    <h5 className="snapshot-title before">
                                      변경 전
                                    </h5>
                                    <div className="snapshot-content">
                                      {history.beforeSnapshot ? (
                                        <HistorySnapshot
                                          snapshot={history.beforeSnapshot}
                                          changedFields={
                                            history.changedFields || []
                                          }
                                          keywordMap={keywordMap}
                                        />
                                      ) : (
                                        <p>데이터 없음</p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="snapshot-section">
                                    <h5 className="snapshot-title after">
                                      변경 후
                                    </h5>
                                    <div className="snapshot-content">
                                      {history.afterSnapshot ? (
                                        <HistorySnapshot
                                          snapshot={history.afterSnapshot}
                                          changedFields={
                                            history.changedFields || []
                                          }
                                          keywordMap={keywordMap}
                                        />
                                      ) : (
                                        <p>데이터 없음</p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 닫기 버튼 */}
          <div className="modal-footer">
            {user?.role !== "CHECKER" &&
              (adData.status === "APPROVED" || adData.status === "PENDING") && (
                <button
                  className="ad-detail-edit-keyword-btn"
                  onClick={() => {
                    setIsKeywordEditModalOpen(true);
                  }}
                >
                  키워드 수정
                </button>
              )}
            {user?.role !== "CHECKER" && adData.status === "APPROVED" && (
                <button
                  className="ad-detail-increase-quantity-btn"
                  onClick={() => {
                    if (getRemainingDaysUntilEnd() < 5) {
                      alert(
                        "수량 증가는 광고 종료일이 5일 이상 남았을 때만 가능합니다."
                      );
                      return;
                    }
                    setIsQuantityIncreaseModalOpen(true);
                  }}
                >
                  수량 증가
                </button>
              )}
            {user?.role !== "CHECKER" && adData.status === "PENDING" && (
              <button
                className="ad-detail-cancel-btn"
                onClick={handleCancelAd}
                disabled={isCancelling}
                // style={{
                //   backgroundColor: "#dc3545",
                //   color: "white",
                //   border: "none",
                //   padding: "8px 16px",
                //   borderRadius: "4px",
                //   cursor: isCancelling ? "not-allowed" : "pointer",
                //   opacity: isCancelling ? 0.6 : 1,
                // }}
              >
                {isCancelling ? "취소 중..." : "등록 취소"}
              </button>
            )}
            <button className="ad-detail-close-btn" onClick={onClose}>
              닫기
            </button>
          </div>
        </div>

        {isExtensionModalOpen && (
          <AdExtensionModal
            isOpen={isExtensionModalOpen}
            onClose={() => setIsExtensionModalOpen(false)}
            selectedAds={[adData]}
            onExtend={(result) => {
              if (result === "success") {
                // 연장 성공 시 모달 닫기
                setIsExtensionModalOpen(false);
                // 부모 컴포넌트에 연장 완료 알림 및 데이터 새로고침
                alert("광고 연장이 성공적으로 완료되었습니다!");
                // 부모 컴포넌트의 데이터 새로고침 함수 호출
                if (onSave) {
                  onSave();
                }
              }
            }}
            unitPrices={unitPrices}
            username={username}
            user={userData}
            getUserDetailAPI={getUserDetailAPI}
          />
        )}

        {/* 키워드 수정 모달 */}
        {isKeywordEditModalOpen && (
          <SimpleKeywordEditModal
            isOpen={isKeywordEditModalOpen}
            onClose={() => setIsKeywordEditModalOpen(false)}
            adData={adData}
            keywordData={keywordData}
            onSave={onSave}
          />
        )}

        {/* 수량 증가 모달 */}
        {isQuantityIncreaseModalOpen && (
          <QuantityIncreaseModal
            isOpen={isQuantityIncreaseModalOpen}
            onClose={() => setIsQuantityIncreaseModalOpen(false)}
            adData={adData}
            unitPrice={getUnitPrice(adData)}
            onSave={onSave}
          />
        )}
      </div>
    </>
  );
};

// 내역 스냅샷 컴포넌트
const HistorySnapshot = ({ snapshot, changedFields = [], keywordMap = {} }) => {
  const getStatusLabel = (status) => {
    switch (status) {
      case "PENDING":
        return "승인대기중";
      case "APPROVED":
        return "진행중";
      case "PAUSED":
        return "일시정지";
      case "REJECTED":
        return "거절";
      case "DONE":
        return "종료";
      case "REFUND":
        return "환불";
      default:
        return status;
    }
  };

  const isChanged = (fieldName) => {
    return changedFields.includes(fieldName);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "boolean") return value ? "예" : "아니오";
    if (Array.isArray(value)) return value.join(", ");
    return value;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleString("ko-KR");
    } catch (error) {
      return dateString;
    }
  };

  let memoData = {};
  try {
    if (snapshot.memo) {
      memoData = JSON.parse(snapshot.memo);
    }
  } catch (error) {
    console.error("memo 파싱 실패:", error);
  }

  const renderField = (
    label,
    fieldName,
    value,
    formatter = null,
    forceChanged = false
  ) => {
    const formattedValue = formatter ? formatter(value) : formatValue(value);
    const changed = forceChanged || isChanged(fieldName);

    return (
      <div className="snapshot-row" key={fieldName + label}>
        <span className="snapshot-label">{label}:</span>
        <span className={`snapshot-value ${changed ? "changed-field" : ""}`}>
          {formattedValue}
        </span>
      </div>
    );
  };

  return (
    <div className="snapshot-data">
      {renderField("서비스명", "serviceName", snapshot.serviceName)}
      {renderField("광고명", "missionName", snapshot.missionName)}
      {renderField("미션 브라우저", "openWebSite", snapshot.openWebSite)}
      {renderField("웹 회원 ID", "webMemberId", snapshot.webMemberId)}
      {renderField("상품명", "productName", snapshot.productName)}
      {renderField("스토어명", "storeName", snapshot.storeName)}
      {renderField("상품 URL", "productUrl", snapshot.productUrl)}
      {renderField("상품 ID", "productId", snapshot.productId)}
      {renderField("원부 ID", "originId", snapshot.originId)}
      {renderField("이미지 URL", "imageUrl", snapshot.imageUrl)}
      {renderField("상태", "status", getStatusLabel(snapshot.status))}
      {renderField(
        "가격",
        "price",
        snapshot.price ? `${snapshot.price.toLocaleString()}원` : "-"
      )}
      {renderField("총 수량", "totalQuantity", snapshot.totalQuantity)}
      {renderField("일일 수량", "dailyQuantity", snapshot.dailyQuantity)}
      {renderField("시작일", "startDate", formatDate(snapshot.startDate))}
      {renderField("종료일", "endDate", formatDate(snapshot.endDate))}
      {renderField(
        "메인 키워드",
        "mainKeywordId",
        snapshot.mainKeywordId
          ? `${keywordMap[snapshot.mainKeywordId] || "알 수 없음"} (ID: ${
              snapshot.mainKeywordId
            })`
          : "-"
      )}
      {renderField(
        "서브 키워드",
        "subKeywordIds",
        snapshot.subKeywordIds && snapshot.subKeywordIds.length > 0
          ? snapshot.subKeywordIds
              .map((id) => `${keywordMap[id] || "알 수 없음"} (ID: ${id})`)
              .join(", ")
          : "-"
      )}
      {renderField("미션 분할", "divisionCount", snapshot.divisionCount)}
      {renderField("생성일", "createdAt", formatDate(snapshot.createdAt))}
      {renderField("수정일", "updatedAt", formatDate(snapshot.updatedAt))}
      {memoData.originProductName !== undefined &&
        renderField(
          "원본 상품명",
          "memo",
          memoData.originProductName,
          null,
          isChanged("memo")
        )}
      {memoData.originPrice !== undefined &&
        renderField(
          "원가",
          "memo",
          memoData.originPrice,
          null,
          isChanged("memo")
        )}
      {memoData.unitPrice !== undefined &&
        renderField(
          "적용단가",
          "memo",
          memoData.unitPrice,
          null,
          isChanged("memo")
        )}
      {memoData.adTag !== undefined &&
        renderField(
          "광고 태그",
          "memo",
          memoData.adTag,
          null,
          isChanged("memo")
        )}
    </div>
  );
};

export default PlaceAdDetail;
